import { animateDOM, disposeStoreOnUnmount } from 'core'
import { z } from 'zod'
import { useRecordingID, useRecordingState } from './data'

export namespace Comment {
  export const Model = z.object({
    comment: z.string(),
    commenter_name: z.string().nullable(),
    commenter_profile_picture_url: z.string().nullable(),
    seconds: z.number(),
    timestamp: z.coerce.date()
  })

  export const InputModel = z.object({
    email: z.string().email().optional(),
    comment: z.string(),
    seconds: z.number()
  })

  export type IModel = z.infer<typeof Model>
  export type IInputModel = z.infer<typeof InputModel>

  export function getList(uid: string): Promise<IModel[]> {
    return http.static.get(http.getURL(`/comments/${uid}/list`))
      .json((data: { comments: IModel[] }) => Model.array().parse(data.comments))
  }

  export function add(uid: string, data: IInputModel) {
    // Append access token if authorized
    const fetch = http.isAuthorized
      ? http.static.auth(`Bearer ${http.tokens.accessToken}`)
      : http.static

    return fetch.post(data, http.getURL(`/comments/${uid}/add`))
      .json(Model.parse)
  }
}

export const useComments = defineStore('listen-page:comments', () => {
  const audioId = useRecordingID()
  const source = useRecordingState()

  const { data: list } = useLazyAsyncData(`comments:${audioId}`, () => Comment.getList(audioId), {
    default: () => <Comment.IModel[]>[],
    getCachedData: (key, nuxt) => nuxt.payload.data[key] || nuxt.static.data[key],
    transform: sortList
  })

  const enabled = computed(() => source.data.value?.settings.comments.enabled === true)
  const showDesktopSidebar = computed(() => enabled.value && list.value.length)

  /** List of hightlighted comments */
  const highlightedList = shallowRef<Comment.IModel[]>([])

  const dialog = reactive({
    enabled: false,
    comments: [] as Comment.IModel[],

    /** Index of active comment */
    activeIndex: 0,

    /** New comment draft */
    draft: {
      time: 0,
      text: ''
    }
  })

  function sortList(input: Comment.IModel[]) {
    input.sort((a, b) => a.seconds - b.seconds)
    return input
  }

  function updateList(input: Comment.IModel[]) {
    animateDOM(() => {
      list.value = sortList(Array.from(input))
    })
  }

  async function add(email?: string) {
    const entry: Comment.IInputModel = {
      email,
      comment: dialog.draft.text.trim(),
      seconds: dialog.draft.time
    }

    const result = await Comment.add(audioId, entry)
    updateList([result, ...list.value])

    // Clear
    dialog.draft.text = ''
  }

  function showDialog(time: number = 0, list: Comment.IModel[] = []) {
    dialog.comments = list
    dialog.enabled = true
    dialog.activeIndex = 0

    dialog.draft.time = time
    dialog.draft.text = ''
  }

  const setHighlightedComments = debounce(100, (list: Comment.IModel[]) => {
    highlightedList.value = list
  })

  // ! Dispose on unmount as this store is page specific
  disposeStoreOnUnmount(useComments)

  return {
    enabled,
    showDesktopSidebar,

    list,
    highlightedList,

    dialog,
    add,
    showDialog,
    setHighlightedComments
  }
})
